import React, { createContext, useState, useEffect, useContext} from "react";
import _ from "lodash";
import Moment from "moment-timezone";
import { ConfigContext } from "../security/ConfigContext";
import JobUtils from "../utils/JobsUtils";
import UserUtils from "../utils/UserUtils";
import useSecurity from "../hooks/useSecurity";
import { useHistory } from "react-router-dom";
import { Status } from "../models/Status";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { UserContext } from "./UserContext";
import ErrorRedirect from "../components/error-redirect/ErrorRedirect";
import useAxios from "../adapter/useAxios";
import { UserRoles } from "../constants/UserRoles";
import useStateRef from "../hooks/useStateRef";
import { SocketContext } from "./SocketContext";
import SocketUtils from "../utils/SocketUtils";

const getMinDate = () => Moment(new Date()).add(24, "hours").tz("America/New_York").format("YYYY-MM-DDTHH:mm:ssZ");
const getDateToday = () => Moment(new Date()).tz("America/New_York").format("YYYY-MM-DDTHH:mm:ssZ");


const TYPES = {
  COPY : "copy",
  REDO : "redo"
}

const PREFIXES = {
  COPY : "COPY---",
  REDO : "REDO---"
}

const JOB_TYPES = {
  DELIVERY: "delivery",
  SOURCE: "source",
  PROCESSOR: "processor"
}

const initialState = {
  loading: true,
  needContentProtection : [],
  hasInvalidAssets : false,
  loadId: "",
  recordId: "",
  jobRecordId: "",
  jobId: "",
  requestId: "",
  requestName: "",
  dueDate: "" + getMinDate(),
  dueTime: "",
  jobType: {},
  sourceLocation: {},
  contacts: [],
  notes: "",
  status: Status.DRAFT,
  requestor: {},
  assignedTo: {},
  createdDate: null,
  sources: [],
  processes: [],
  deliveries: [],
  destination: {},
  deliveriesToSourceMapping: {},
  jobKeysToFilename: {},
  itemList: {},
  jobs: [],
  auditTrail: [],
  isSubmitted: false,
  needsSourceOverride: false,
  unMappedSources: [],
  showRequestForm: false,
  draftSaved: false,
  submittedDate: "",
  requestSummary: {},
  display: true,
  createdBy: "",
  qcRequired: false,
  contentProtection: {},
  type: "",
  showSourceForm: false,
  selectedSource: {},
  showProcessForm: false,
  selectedProcess: {},
  showDeliveryForm: false,
  selectedDelivery: {},
  isStitch: false,
  stitchAssetName: "",
  stitchedKeys: [],
  stitchAssetNameError: false,
  stitchingError: false,
  validatedSources: {},
  ecoProcessError: "",
  unmappedDeliveryKeys: new Set(),
  premiereDateConfirmed:false,
  dcpDateConfirmed:false,
  eventDeliveryFlag:{},
  importError:false,
  downloading: false,
  enableSnackbar: false,
  snackbarMessage: "",
  deliveryMetadataKey: {}
};

export const RequestContext = createContext({
  loading: initialState.loading,
  hasInvalidAssets: initialState.hasInvalidAssets,
  needContentProtection: initialState.needContentProtection,
  _setLoading: (isLoading) => {},
  loadId: initialState.loadId,
  jobId: initialState.jobId,
  _setJobId: (id) => {},
  requestId: initialState.requestId,
  _setRequestId: (id) => {},
  requestName: initialState.requestName,
  _setRequestName: (requestName) => {},
  dueDate: initialState.dueDate,
  _setDueDate: (dueDate) => {},
  dueTime: initialState.dueTime,
  _setDueTime: (dueTime) => {},
  jobType: initialState.jobType,
  _setJobType: (jobType) => {},
  sourceLocation: initialState.sourceLocation,
  _setSourceLocation: (idLocation) => {},
  contacts: initialState.contacts,
  _setContacts: (contacts) => {},
  notes: initialState.notes,
  _setNotes: (notes) => {},
  status: initialState.status,
  _setStatus: (status) => {},
  requestor: initialState.requestor,
  assignedTo: initialState.assignedTo,
  createdDate: initialState.createdDate,
  sources: initialState.sources,
  processes: initialState.processes,
  deliveries: [],
  destination: initialState.destination,
  deliveriesToSourceMapping: initialState.deliveriesToSourceMapping,
  _setDeliveriesToSourceMapping: (deliveriesToSourceMapping) => {},
  jobKeysToFilename: initialState.jobKeysToFilename,
  _setJobKeysToFilename: (jobKeysToFilename) => {},
  itemList: initialState.itemList,
  getItemList: (type, isSource) => {},
  jobs: initialState.jobs,
  auditTrail: initialState.auditTrail,
  unMappedSources: initialState.unMappedSources,
  needsSourceOverride: initialState.needsSourceOverride,
  _setUnMappedSources: (sourcesArray) => {},
  onLoadRequest: (id: String) => {},
  requestDraft: () => {},
  submitRequest: () => {},
  addNewSource: (source, callback) => {},
  addNewSources: (sourceList, callback) => {},
  addNewProcess: (process) => {},
  addNewProcesses: (processList) => {},
  addNewDelivery: (delivery) => {},
  addNewDeliveries: (deliveryList) => {},
  updateSource: (source, callback) => {},
  updateProcess: (process) => {},
  updateDelivery: (delivery) => {},
  deleteSource: (source) => {},
  deleteProcess: (process) => {},
  deleteDelivery: (delivery) => {},
  setIsStitch: (isStitch) => {},
  setStitchAssetName: (stitchAssetName) => {},
  setStitchedKeys: (stitchedKeys) => {},
  setStitchAssetNameError: (stitchAssetNameError) => {},
  setStitchingError: (stitchingError) => {},
  updateJob: () => {},
  _setLoadId: (id) => {},
  isStitch: initialState.isStitch,
  stitchAssetName: initialState.stitchAssetName,
  stitchedKeys: initialState.stitchedKeys,
  stitchAssetNameError: initialState.stitchAssetNameError,
  stitchingError: initialState.stitchingError,
  showRequestForm: initialState.showRequestForm,
  _setShowRequestForm: (flag) => {},
  draftSaved: initialState.draftSaved,
  submittedDate: initialState.submittedDate,
  requestSummary: initialState.requestSummary,
  display: initialState.display,
  _setDisplay: (show) => {},
  createdBy: initialState.createdBy,
  qcRequired: initialState.qcRequired,
  contentProtection: initialState.contentProtection,
  _setContentProtection: (contentProtectionData) => {},
  type: initialState.type,
  _setType: (type) => {},
  _setShowSourceForm: (flag) => {},
  showSourceForm: initialState.showSourceForm,
  _setSelectedSource: (source) => {},
  selectedSource: initialState.selectedSource,
  _setShowProcessForm: (flag) => {},
  showProcessForm: initialState.showProcessForm,
  _setSelectedProcess: (process) => {},
  selectedProcess: initialState.selectedProcess,
  _setShowDeliveryForm: (flag) => {},
  showDeliveryForm: initialState.showDeliveryForm,
  _setSelectedDelivery: (delivery) => {},
  selectedDelivery: initialState.selectedDelivery,
  ecoProcessError: initialState.ecoProcessError,
  _setEcoProcessError: (error) => {},
  loadParameters: () => {},
  unmappedDeliveryKeys: initialState.unmappedDeliveryKeys,
  _setUnmappedDeliveryKeys: (deliveryKeySet) => {},
  premiereDateConfirmed:initialState.premiereDateConfirmed,
  setPremiereDateConfirmed:(flag)=>{},
  dcpDateConfirmed:initialState.dcpDateConfirmed,
  setDcpDateConfirmed:(flag)=>{},
  eventDeliveryFlag:initialState.eventDeliveryFlag,
  setEventDeliveryFlag:(flag)=>{},
  importError:initialState.importError,
  setImportError:(flag)=>{},
  downloading: initialState.downloading,
  enableSnackbar: initialState.enableSnackbar,
  _setEnableSnackbar: (flag)=> {},
  snackbarMessage: initialState.snackbarMessage,
  getTemplate: (sourceLocation) => {},
  deliveryMetadataKey: initialState.deliveryMetadataKey,
  _setDeliveryMetadataKey: (metadata) => {}
});

let counter = 0;

export const RequestContextProvider = ({ children }) => {
  const [config] = useContext(ConfigContext);
  const {_setMessageToSocket} = useContext(SocketContext)
  const history = useHistory();
  const { userInfo } = useSecurity();
  const [loading, setLoading] = useState(initialState.loading);
  const [hasInvalidAssets, setHasInvalidAssets] = useState(initialState.hasInvalidAssets);
  const [needContentProtection, setNeedContentProtection] = useState(initialState.needContentProtection);
  // Initial states of sources, processes & deliveries
  const [loadId, setLoadId] = useState(initialState.loadId);
  const [recordId, setRecordId] = useStateRef(initialState.recordId);
  const [jobRecordId, setJobRecordId] = useStateRef(initialState.jobRecordId);
  const [requestId, setRequestId] = useStateRef(initialState.requestId);
  const [jobId, setJobId] = useStateRef(initialState.jobId);
  const [requestName, setRequestName] = useState(initialState.requestName);
  const [dueDate, setDueDate] = useState(initialState.dueDate);
  const [dueTime, setDueTime] = useState(initialState.dueTime);
  const [jobType, setJobType] = useState(initialState.jobType);
  const [sourceLocation, setSourceLocation] = useState(initialState.sourceLocation);
  const [contacts, setContacts] = useState(initialState.contacts);
  const [notes, setNotes] = useState(initialState.notes);
  const [status, setStatus] = useState(initialState.status);
  const [requestor, setRequestor] = useStateRef(initialState.requestor);
  const [assignedTo, setAssignedTo] = useState(initialState.assignedTo);
  const [createdDate, setCreatedDate] = useStateRef(initialState.createdDate);
  const [sources, setSources] = useState(initialState.sources);
  const [processes, setProcesses] = useState(initialState.processes);
  const [deliveries, setDeliveries] = useState(initialState.deliveries);
  const [destination, setDestination] = useState(initialState.destination);
  const [deliveriesToSourceMapping, setDeliveriesToSourceMapping] = useState(initialState.deliveriesToSourceMapping);
  const [jobKeysToFilename, setJobKeysToFilename] = useState(initialState.jobKeysToFilename);
  const [jobs, setJobs] = useState(initialState.jobs);
  const [auditTrail, setAuditTrail] = useState(initialState.auditTrail);
  const [submittedDate, setSubmittedDate] = useStateRef(initialState.submittedDate);
  const [unMappedSources, setUnMappedSources] = useState(initialState.unMappedSources);
  const [needsSourceOverride, setNeedsSourceOverride] = useState(initialState.needsSourceOverride);
  const [showRequestForm, setShowRequestForm] = useState(initialState.showRequestForm);
  const [draftSaved, setSaved] = useState(initialState.draftSaved);
  const [requestSummary, setRequestSummary] = useStateRef(initialState.requestSummary);
  const [display, setDisplay] = useState(initialState.display);
  const [createdBy, setCreatedBy] = useStateRef(initialState.createdBy);
  const [itemList, setItemList] = useState(initialState.itemList);
  const [qcRequired, setQcRequired] = useState(initialState.qcRequired);
  const [contentProtection, setContentProtection] = useState(initialState.contentProtection);
  const [type, setType] = useState(initialState.type);
  const [showSourceForm, setShowSourceForm] = useState(initialState.showSourceForm);
  const [selectedSource, setSelectedSource] = useState(initialState.selectedSource);
  const [showProcessForm, setShowProcessForm] = useState(initialState.showProcessForm);
  const [selectedProcess, setSelectedProcess] = useState(initialState.selectedProcess);
  const [showDeliveryForm, setShowDeliveryForm] = useState(initialState.showDeliveryForm);
  const [selectedDelivery, setSelectedDelivery] = useState(initialState.selectedDelivery);
  const [isStitch, setIsStitch] = useState(initialState.isStitch);
  const [stitchAssetName, setStitchAssetName] = useState(initialState.stitchAssetName);
  const [stitchedKeys, setStitchedKeys] = useState(initialState.stitchedKeys);
  const [stitchAssetNameError, setStitchAssetNameError] = useState(initialState.stitchAssetNameError);
  const [stitchingError, setStitchingError] = useState(initialState.stitchingError);
  const [validatedSources, setValidatedSources] = useState(initialState.validatedSources);
  const [ecoProcessError, setEcoProcessError] = useState(initialState.ecoProcessError);
  const [unmappedDeliveryKeys, setUnmappedDeliveryKeys] = useState(initialState.unmappedDeliveryKeys);
  const [premiereDateConfirmed, setPremiereDateConfirmed] = useState(false);
  const [dcpDateConfirmed, setDcpDateConfirmed] = useState(false);
  const [eventDeliveryFlag,setEventDeliveryFlag]=useState({});
  const [importError,setImportError]=useState(initialState.importError);  
  const [downloading,setDownloading]=useState(initialState.downloading); 
  const [snackbarMessage,setSnackbarMessage]=useState(initialState.snackbarMessage); 
  const [enableSnackbar,setEnableSnackbar]=useState(initialState.enableSnackbar);
  const [deliveryMetadataKey, setDeliveryMetadataKey]=useState(initialState.deliveryMetadataKey); 
  const {get, post, put} = useAxios();
  const { userRole,isUserLoading, faspexAccountMapping } = useContext(UserContext);
  const isUploadOnlyHdr = JobUtils.extractJobTypeProperty(jobType, "jobType") === 'UPLOAD_ONLY' && _.get(sourceLocation,'value','') === 'HDR_MAM';

  const _setStitchAssetName=(value)=>{
    setStitchAssetName(value);
    const jobtype = JobUtils.extractJobTypeProperty(jobType, "jobType");
    const cloneMapping ={ ...deliveriesToSourceMapping };
    let sourceList = [...sources];
    sourceList.forEach((source) => {
      let key = _.get(source, "key");
      reMap(jobtype, source, key, cloneMapping,value);
    });
    setDeliveriesToSourceMapping(Object.assign({}, cloneMapping));
  }
  const _setShowRequestForm = (flag) => {
    setShowRequestForm(flag);
  };

  const _setDeliveryMetadataKey = (metadata) => {
    setDeliveryMetadataKey(metadata);
  };

  const _setContentProtection = (contentProtectionData) => {
    setContentProtection(contentProtectionData);
  };
  const updateCounter = (source) => {
    const key = "key";
    _.set(source, key, counter);
    counter++;
  };
  const _setDisplay = (show: boolean) => {
    setDisplay(show);
  };

  const _setType = (type) => {
    setType(type);
  }

  const _setShowSourceForm = (flag) => {
    setShowSourceForm(flag);
  };

  const _setSelectedSource = (source) => {
    setSelectedSource(source);
  }

  const _setShowProcessForm = (flag) => {
    setShowProcessForm(flag);
  }

  const _setSelectedProcess = (process) => {
    setSelectedProcess(process);
  }

  const _setShowDeliveryForm = (flag) => {
    setShowDeliveryForm(flag);
  }

  const _setSelectedDelivery = (delivery) => {
    setSelectedDelivery(delivery);
  }

  const _setEcoProcessError = (error) => {
    setEcoProcessError(error);
  }

  const _setUnmappedDeliveryKeys = (deliveryKeySet) => {
    setUnmappedDeliveryKeys(deliveryKeySet);
  }

  const _setUnMappedSources = (sourcesArray) => {
    setUnMappedSources(sourcesArray);
  };

  const _setSourceLocation = (idLocation) => {
    if (_.get(idLocation, "type") === "ASPERA" && !_.has(itemList, "Aspera AoC Accounts")) {
      getItemList("Aspera AoC Accounts");
    }
    setSourceLocation(idLocation);
    setIsStitch(false);
  };

  const _setEnableSnackbar = (flag) => {
    setEnableSnackbar(flag);
  };

  const addNewSource = (source, callback) => {
    let key = _.get(source, "key");
    //Below condition will make sure any one id is present in UploadOnly HDR
    if(isUploadOnlyHdr) {
      if(source['sourceFileIdType'] === 'XMN' && source['titleId']) {
        delete source['titleId'];
      }else if(source['sourceFileIdType'] === 'Title ID' && source['assetId']) {
        delete source['assetId'];
      }
    }
    if (!_.isNil(key)) {
      let index = _.findIndex(sources, { key: key });
      if (index > -1) {
        updateSource(source, callback);
        return;
      }
    }
    trimObjectValues(source);
    updateCounter(source);    
    let sourceList = [source, ...sources];
    validateAssets(
      JobUtils.extractJobTypeProperty(jobType, "jobType"),
      sourceLocation,
      source,
      sourceList
    );
    callback();
  };
  
  const addNewSources = (sourceList, callback) => {
    let clonedSources = [...sources];
    for (let source of sourceList) {
      trimObjectValues(source);
      let key = _.get(source, "key");
      if (_.isNil(key)) {
        updateCounter(source);
        clonedSources.unshift(source);
      } else {
        let index = _.findIndex(sources, { key: key });
        if (index < 0) {
          updateMappingFileNameExt(source);
          clonedSources[index] = source;
        } else {
          updateCounter(source);
          clonedSources.unshift(source);
        }
      }      
    }    
    for (let source of sourceList) {
      validateAssets(
          JobUtils.extractJobTypeProperty(jobType, "jobType"),
          sourceLocation,
          source,
          clonedSources);
    }
    if (callback) {
      callback();
    }
  };

  const addNewProcess = (process) => {
    let key = _.get(process, "key");
    if (!_.isNil(key)) {
      let index = _.findIndex(processes, { key: key });
      if (index > -1) {
        updateProcess(process);
        return;
      }
    }
    trimObjectValues(process);
    updateCounter(process);
    setProcesses([process, ...processes]);
  };

  const addNewProcesses = (processList) => {
    let clonedProcesses = [...processes];
    for (let process of processList) {
      let key = _.get(process, "key");
      trimObjectValues(process);
      if (_.isNil(key)) {
        updateCounter(process);
        clonedProcesses.unshift(process);
      } else {
        let index = _.findIndex(processes, { key: key });
        if (index < 0) {
          clonedProcesses[index] = process;
        } else {
          updateCounter(process);
          clonedProcesses.unshift(process);
        }
      }

    }
    setProcesses(clonedProcesses);
  }

  const addNewDelivery = (delivery) => {
    let key = _.get(delivery, "key");
    if (!_.isNil(key)) {
      let index = _.findIndex(deliveries, { key: key });
      if (index > -1) {
        updateDelivery(delivery);
        return;
      }
    }
    trimObjectValues(delivery);
    updateCounter(delivery);
    setDeliveries([delivery, ...deliveries]);
  };

  const addNewDeliveries = (deliveryList) => {
    let clonedDeliveries = [...deliveries];
    for (let delivery of deliveryList) {
      let key = _.get(delivery, "key");
      trimObjectValues(delivery);
      if (_.isNil(key)) {
        updateCounter(delivery);
        clonedDeliveries.unshift(delivery);
      } else {
        let index = _.findIndex(sources, { key: key });
        if (index < 0) {
          clonedDeliveries[index] = delivery;
        } else {
          updateCounter(delivery);
          clonedDeliveries.unshift(delivery);
        }
      }
    }
    setDeliveries(clonedDeliveries);
  }

  const deleteSource = (source) => {
    let key = _.get(source, "key");

    let cloneContentProtection = { ...contentProtection };
    delete cloneContentProtection[key];
    setContentProtection(cloneContentProtection);

    let list = _.filter(sources, (source) => {
      return _.get(source, "key") !== key;
    });    
    setSources(
        list
    );
    setStitchedKeys(stitchedKeys.filter(k => k !== key));
    let cloneMapping = { ...deliveriesToSourceMapping };
    processes.length > 0 ?
        _.map(processes, (process) => {
          let mappedKey = key;
          if (!_.isNil(_.get(process, "key"))) {
            mappedKey += "_" + _.get(process, "key");
          }
          delete cloneMapping[mappedKey];
        }) : delete cloneMapping[key];
    setDeliveriesToSourceMapping(Object.assign({}, cloneMapping));
  };

  const deleteProcess = (process) => {
    let key = _.get(process, "key");

    setProcesses(
        _.filter(processes, (data) => {
          return _.get(data, "key") !== key;
        })
    );

    let cloneMapping = { ...deliveriesToSourceMapping };
    _.map(sources, (source) => {
      let mappedKey = _.get(source, "key") + "_" + key;
      delete cloneMapping[mappedKey];
    })
    setDeliveriesToSourceMapping(Object.assign({}, cloneMapping));
  };

  const deleteDelivery = (delivery) => {
    let key = _.get(delivery, "key");
    setDeliveries(
        _.filter(deliveries, (del) => {
          return _.get(del, "key") !== key;
        })
    );
    let cloneMapping = { ...deliveriesToSourceMapping };
    let sourceKeySet = _.keys(cloneMapping);
    _.map(sourceKeySet, (sourceKey) => {
      let sourceKeyValue = cloneMapping[sourceKey];
      let filterResult = _.filter(sourceKeyValue, (delKey) => {
        return delKey !== key;
      });
      if (_.size(filterResult) === 0) {
        delete cloneMapping[sourceKey];
      } else {
        cloneMapping[sourceKey] = filterResult;
      }
    });
    setDeliveriesToSourceMapping(Object.assign({}, cloneMapping));
  };

  const updateSource = (source, callback) => {
    trimObjectValues(source);
    let key = _.get(source, "key");
    let index = _.findIndex(sources, { key: key });
    let clonedSources = [...sources];    
    clonedSources[index] = source;
    updateMappingFileNameExt(source);    
    const cloneMapping = {...deliveriesToSourceMapping};
    const jobtype = JobUtils.extractJobTypeProperty(jobType, "jobType"); 
    validateAssets(
      JobUtils.extractJobTypeProperty(jobType, "jobType"),
      sourceLocation,
      source,
      clonedSources,
    );
    reMap(jobtype,source,key,cloneMapping);
    setDeliveriesToSourceMapping(Object.assign({}, cloneMapping));
    callback();
  };

  const reMap=(jobtype,source,key,cloneMapping,stitchValue='')=>{
    const regexPattern = new RegExp(/[^a-zA-Z0-9-_. ]/g); 
    if(JobUtils.skipValidationByJobType(jobtype)){
      if(processes.length > 0) {
        _.map(processes, (process) => {
          let mapping = !_.isEmpty(process) ? _.get(source, "key") + "_" + _.get(process, "key") : _.get(source, "key");
          let mappedKey = key;
          if (!_.isNil(_.get(process, "key"))) {
            mappedKey += "_" + _.get(process, "key");
          }
          const newFileName = JobUtils.getDefaultFileName(jobType, sourceLocation, source, _.get(jobKeysToFilename, mapping), process, stitchValue ? stitchValue : stitchAssetName, isStitch);
          if (newFileName && newFileName.match(regexPattern)) { 
            delete cloneMapping[mappedKey];
          }
        })
      }else{
          let mapping = _.get(source, "key");
        const newFileName = JobUtils.getDefaultFileName(jobType, sourceLocation, source, _.get(jobKeysToFilename, mapping), '', stitchValue ? stitchValue : stitchAssetName, isStitch);
        if (newFileName && newFileName.match(regexPattern)) { 
            delete cloneMapping[key];
          }
      }
    } 
  }

  const updateMappingFileNameExt = (source) => {
    //Need to update extension
    if (!_.isEmpty(_.get(jobKeysToFilename, _.get(source, "key"), ""))) {
      let clonnedJobKeysToFileName = { ...jobKeysToFilename };
      let mappedName = clonnedJobKeysToFileName[_.get(source, "key")];
      let sourceFileName = JobUtils.getSourceFileName(source, jobType);
      let sourceExt = sourceFileName.split(".").length > 1 ? sourceFileName.split(".")[1] : "";

      //Source ext. is empty then remove the ext. from mapped name if exhist
      if (_.isEmpty(sourceExt)) {
        if (mappedName.split(".").length > 1) {
          clonnedJobKeysToFileName[_.get(source, "key")] = mappedName.split(".")[0];
          setJobKeysToFilename(clonnedJobKeysToFileName);
        }
      } else {
        let pos = mappedName.lastIndexOf(".");
        mappedName = mappedName.substr(0, pos < 0 ? mappedName.length : pos) + "." + sourceExt;
        clonnedJobKeysToFileName[_.get(source, "key")] = mappedName;
        setJobKeysToFilename(clonnedJobKeysToFileName);
      }
    }
  };

  const updateProcess = (process) => {
    trimObjectValues(process);
    let key = _.get(process, "key");
    let index = _.findIndex(processes, { key: key });
    let clonedProcesses = [...processes];
    clonedProcesses[index] = process;
    setProcesses(clonedProcesses);
  };

  const updateDelivery = (delivery) => {
    trimObjectValues(delivery);
    let key = _.get(delivery, "key");
    let index = _.findIndex(deliveries, { key: key });
    let clonedDeliveries = [...deliveries];
    clonedDeliveries[index] = delivery;
    setDeliveries(clonedDeliveries);
  };
  const resetAll = () => {
    setSources([]);
    setProcesses([]);
    setDeliveries([]);
    setDestination({});
    setDeliveriesToSourceMapping({});
    setStitchedKeys([]);
    setJobKeysToFilename({});
    setJobs([]);   
    _setUnMappedSources([]);
    setSaved(false);
    _setDeliveryMetadataKey({});
  };

  const onLoadRequest = (id: String) => {};

  useEffect(() => {
    if (!_.isEmpty(sourceLocation)) {
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLocation]);

  useEffect(() => {
    let newValidatedSources = {...validatedSources};
    if (_.isEmpty(newValidatedSources)) {
      return;
    }
    let sourceList = [...sources];
    _.map(_.keys(newValidatedSources), (key) => {
      let index = _.findIndex(sourceList, (s) => {
        return _.get(s, "key") === key;
      })
      if (index === -1) {
        delete newValidatedSources[key];
        return;
      }
      let source = sourceList[index];
      delete source['contentProtection'];
      delete source['premiereDateEst'];
      sourceList[index] = { ...source };
      delete newValidatedSources[key];
    });
    setSources(sourceList);
    setValidatedSources(newValidatedSources);
    const jobtype = JobUtils.extractJobTypeProperty(jobType, "jobType");
    const cloneMapping ={ ...deliveriesToSourceMapping }
    sourceList.forEach((source) => {
      let key = _.get(source, "key");
      reMap(jobtype, source, key, cloneMapping);
    });
    setDeliveriesToSourceMapping(Object.assign({}, cloneMapping));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatedSources]);

  useEffect(() => {
    setLoading(true);
    const fetchData = () => {
    let isLoading = _.has(loadId, "requestId");
    if (_.has(loadId, "jobId") || _.has(loadId, "requestId")) {
      let loadPath = _.has(loadId, "jobId") ? config.apiUrls.jobs + _.get(loadId, "jobId") : config.apiUrls.requests + _.get(loadId, "requestId");
      get({
        url: loadPath
      })
      .then((data) => {
        const response = JobUtils.filterRequestResponse(data)
        setDisplay(true);
        if (isLoading) {
          populateDraftData(response);
        } else {
          populateJobData(response);
        }
        if (_.has(loadId, "jobId") && JobUtils.getSourceType(_.get(response, "source")) === "ASPERA") {
          getItemList("Aspera AoC Accounts");
        }
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoading(false);
      })
    } else {
      setLoading(false);
    }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadId]);

  const getItemList =  (type) => {
    _setLoading(true);
    get({
      url: config.apiUrls.referenceData + "/" + encodeURI(type)
    })
    .then((response) => {
      let clonned = {...itemList}
      clonned[type] = response;
      setItemList(clonned);
    })
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      _setLoading(false);
    })
  };

  /* Populating job data   */
  const populateJobData = (job) => {
    let requests = _.get(job, "requestSummary");
    setRequestSummary(requests);
    setSubmittedDate(_.get(requests, "submittedDate"));
    setJobRecordId(_.get(job, "_id"));
    setRecordId(_.get(job, "requests[0]._id"));
    setRequestId(_.get(requests, "requestId"));
    setRequestName(_.get(requests, "name"));
    setJobId(_.get(job, "jobId"));
    setDueTime(_.get(job, "dueTime", ""));
    const dueDate = _.get(job, "dueDate");
    setDueDate(!_.isNil(dueDate) ? dueDate : getDateToday());
    setStatus(_.get(job, "status"));
    setNeedsSourceOverride(_.get(job, "needsSourceOverride", null));
    setQcRequired(_.get(job, "qcRequired", null));
    setJobType(JobUtils.extractJobTypeObjectByValue(_.get(job, "jobType")));
    _setSourceLocation(
      job?.source?.sourceLocation?.newLocationSubType ?  {newLocationSubType: _.get(job, "source.sourceLocation.newLocationSubType"),...JobUtils.extractSourceTypeObjectByValue(
            _.get(job, "jobType"),
            _.get(job && job.source, "sourceLocation.locationSubType") || _.get(job, "requests[0].sourceType")
        )} : JobUtils.extractSourceTypeObjectByValue(
            _.get(job, "jobType"),
            _.get(job && job.source, "sourceLocation.locationSubType") || _.get(job, "requests[0].sourceType")
        )
    );
    setContacts(_.get(job, "requests[0].notificationList", []));
    setNotes(_.get(job, "requests[0].notes"));
    setAuditTrail(_.get(job, "auditTrail"));
    setAssignedTo(_.get(job, "assignedTo", {}));
    setRequestor(_.get(job, "requests[0].requestor", {}));
    setCreatedDate(_.get(job, "createdDate", {}));
    setCreatedBy(_.get(job, "createdBy", ""));
    setJobsByType([job]);
  };

  /* Populating draft request data   */
  const populateDraftData = (requestData) => {
    if (type === TYPES.COPY || type === TYPES.REDO) {
      if (type === TYPES.COPY) {
        setRequestName(type === TYPES.COPY ? PREFIXES.COPY + _.get(requestData, "name") : PREFIXES.REDO + _.get(requestData, "name"));
        setRequestor(UserUtils.transformUserInfo(userInfo));
      } else if (type === TYPES.REDO) {
        setRequestName(PREFIXES.REDO + _.get(requestData, "name"));
        setRequestor(_.get(requestData, "requestor"));
      }
      setDueDate(getMinDate());
      // Reset QC checklist for copy and redos
      _.forEach(_.get(requestData, "jobs", []), job => {
        delete job.processor.transcodeQualityCheck;
        delete job.jobId;
        job.processor.filenameChanged = false;
      });
    } else {
      setCreatedDate(_.get(requestData, "createdDate"));
      setRecordId(_.get(requestData, "_id"));
      setRequestId(_.get(requestData, "requestId"));
      setCreatedBy(_.get(requestData, "createdBy"));
      setRequestor(UserUtils.transformUserInfo(userInfo));
      setRequestName(_.get(requestData, "name"));
      const dueDate = _.get(requestData, "dueDate", _.get(requestData, "jobs.[0].dueDate"));
      setDueDate(!_.isNil(dueDate) ? dueDate : getDateToday());
    }
    setDueTime(_.get(requestData.jobs[0], "dueTime", ""));
    setJobType(JobUtils.extractJobTypeObjectByValue(_.get(requestData, "jobType")));
    _setSourceLocation(JobUtils.extractSourceTypeObjectByValue(_.get(requestData, "jobType"), _.get(requestData, "sourceType")));
    setContacts(_.get(requestData, "notificationList", []));
    setNotes(_.get(requestData, "notes"));
    setJobsByType(_.get(requestData, "jobs", []))
  };

  const setJobsByType = (jobs) => {
    let _deliveryToSourceMapping = {},
        _jobKeysToFilename = {},
        _sources = [],
        _processes = [],
        _destinations = [],
        contentProtectionSum = {},
        _deliveryMapping = {},
        isCompositeJobAsset = false,
        stitchAssetName = "";
    const stitchedKeys = [];

    // If any job as composite then all jobs should be composite only.
    isCompositeJobAsset = _.get(jobs[0], "source.type") === "CompositeJobAsset";
    if (isCompositeJobAsset) {
      stitchAssetName = _.get(jobs[0], "source.compositeAssetName");
      // Need to find one source which we need to use for drag and drop.
    }
    _.map(jobs, (job) => {
      let jobType = _.get(job, "jobType");
      let source = {};
      const sourceList = isCompositeJobAsset ? _.get(job, "source.simpleJobAssets", []) : [_.get(job, JOB_TYPES.SOURCE)];
      const sourceDef = _.get(JobUtils.extractJobTypeObjectByValue(jobType), "sourceDefinition") ?? [];
      const activeSourceTypes = new Set(sourceDef.filter(d => d.active).map(d => d.type));
      const filteredSourceList = _.filter(sourceList, (src) => _.get(src, "sourceLocation") && activeSourceTypes.has(_.get(src, "sourceLocation.locationSubType")));
      _.map(filteredSourceList, (src) => {
        source = JobUtils.updateSourceField(src);
        if (_.has(source, "contentProtection")) {
          contentProtectionSum = { ...contentProtectionSum, [counter]: _.get(source, "contentProtection")};
        }
        source = addToJobTypeArray(_sources, source, jobType, JOB_TYPES.SOURCE);
      });
      if (isCompositeJobAsset) {
        // We only need to work with one source so choosing the first one.
        source = _.sortBy(_sources, "key")[0];
      }
      let processor = JobUtils.updateProcess(_.get(job, JOB_TYPES.PROCESSOR));
      const isPlaceholderProcess = JobUtils.isPlaceholderProcess(processor, jobType);
      if (!isPlaceholderProcess) {
        let isFileNameChanged = _.get(processor, "filenameChanged", false);
        let addedProcessor = addToJobTypeArray(_processes, processor, jobType, JOB_TYPES.PROCESSOR);
        let key = _.includes(JobUtils.getProcessors(), jobType)
            ? `${_.get(source, "key")}_${_.get(addedProcessor, "key")}`
            : _.get(source, "key");
        _jobKeysToFilename[key] = isFileNameChanged ? _.get(processor, "outputFilename", "") : "";
        _.map(_.get(job, JOB_TYPES.DELIVERY), (del) => {
          if (_.isUndefined(_deliveryToSourceMapping[key])) {
            _deliveryToSourceMapping[key] = [];
          }
          if (!_.isEmpty(_.get(processor, "outputFilename", ""))) {
            // check for duplicate deliveries
            let destination = _.get(del, "destination");
            let deliveryKey = `${_.get(destination, "account", _.get(destination, "locationSubType", ""))}${_.get(destination, "deliveryOption", "")}`;
            if (!_deliveryMapping[deliveryKey]) {
              updateCounter(del);
              _destinations.push(JobUtils.updateDelivery(del));
              _deliveryMapping[deliveryKey] = del;
            }
            _deliveryToSourceMapping[key].push(_.get(_deliveryMapping[deliveryKey] , "key", ""));
          }
        });
      }
    });

    if (isCompositeJobAsset) {
      setIsStitch(true);
      setStitchAssetName(stitchAssetName);
      _.uniqBy(_sources, "key").forEach((source, index) => {
        stitchedKeys.push(source.key);
      });
      setStitchedKeys(stitchedKeys);
    }

    setContentProtection(contentProtectionSum);
    setDeliveriesToSourceMapping(_deliveryToSourceMapping);
    setJobKeysToFilename(_jobKeysToFilename);
    setSources(_.uniqBy(_sources, "key"));
    setProcesses(_.uniqBy(_processes, "key"));
    setDeliveries(_.uniqBy(_destinations, "key"));
  }

  useEffect(() => {
    if (_.isEmpty(loadId)) {
      _setSourceLocation({});
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobType]);

  const addToJobTypeArray = (array, object, processorJobType, jobType) => {
    const toOmit = ["key", "outputFilename", "filenameChanged"];
    for (let o of array) {
      if (JSON.stringify(_.omit(o, toOmit)) === JSON.stringify(_.omit(object, toOmit))) {
        return o;
      }
    }
    array.push(object);
    if (_.includes(JobUtils.getProcessors(), processorJobType) || jobType === JOB_TYPES.SOURCE) {
      updateCounter(object);
    }
    return object;
  }

  const _setJobId = (id) => {
    setJobId(id);
  };

  const _setRequestId = (id) => {
    setRequestId(id);
  };

  const _setRequestName = (requestName) => {
    setRequestName(requestName);
  };

  const _setDueDate = (dueDate) => {
    setDueDate(dueDate);
  };

  const _setDueTime = (dueTime) => {
    setDueTime(dueTime);
  };

  const _setJobType = (jobType) => {
    setJobType(jobType);
  };  

  const _setContacts = (contacts) => {
    setContacts(contacts);
  };

  const _setNotes = (notes) => {
    setNotes(notes);
  };

  const _setStatus = (status) => {
    setStatus(status);
  };

  const _setJobKeysToFilename = (jobKeysToFilename) => {
    setJobKeysToFilename(jobKeysToFilename);
  };

  const _setDeliveriesToSourceMapping = (deliveriesToSourceMapping) => {
    setDeliveriesToSourceMapping(deliveriesToSourceMapping);
  };

   const getDestinations = () => {
    let destinations = {};
    destinations["deliveryOptions"] = deliveries;
    destinations["sourceToDeliveryMapping"] = deliveriesToSourceMapping;
    destinations["jobKeysToFilename"] = jobKeysToFilename;
    return destinations;
  };

  const getRequestPayload = (isSubmitted, requestorForPayload) => {
    setNotes(notes.trim());
    let requestModel = JobUtils.getRequestPayload(
        requestName,
        !_.isEmpty(requestId) ? requestId : null,
        !_.isEmpty(recordId) ? recordId : null,
        JobUtils.extractJobTypeProperty(jobType, "jobType"),
        JobUtils.extractSourceLocationProperty(sourceLocation, "type"),
        isSubmitted,
        notes,
        createdDate,
        createdBy,
        null,
        null,
        contacts,
        requestorForPayload,
        submittedDate
    );
    return requestModel;
  };

  const getJobsPayload = (status, requestorForPayload) => {
    let jobs = JobUtils.composeJobs(
      dueDate,
      dueTime,
      !_.isEmpty(requestId) ? requestId : null,
      JobUtils.extractJobTypeProperty(jobType, "jobType"),
      status,
      createdDate,
      null,
      requestorForPayload,
      sources,
      processes && processes.length > 0 ? processes : null,
      getDestinations(),
      null,
      null,
      JobUtils.extractSourceLocationProperty(sourceLocation, "type"),
      createdBy,
      sourceLocation,
      jobType,
      contentProtection,
      stitchAssetName,
      isStitch,
      stitchedKeys,
      faspexAccountMapping
    );
    return jobs;
  };

  const getTemplate = (sourceLocation) => {
    setDownloading(true);
    get({ 
      url: config.apiUrls.jobTemplate + _.get(sourceLocation, "template"),
    })
      .then((response) => {
        setDownloading(false);
        if (response.url) {
          window.open(response.url, "_parent");
        }
      })
      .catch((err) => {
        console.log(err);
        if (err) {
          setDownloading(false);
          setSnackbarMessage("Unable to export template at this time");
          _setEnableSnackbar(true);
        }
      });
  };

const checkForContentProtection = () => {
  const items = [];
  _.forEach(sources, function(source, index) {
    if(_.has(source, 'assetIdType') && _.get(source, 'assetIdType') === "No ID Available"){
      items.push(source);
    }
  });
  setNeedContentProtection(items);
}

const checkInvalidAssets = () => {
  let inValidAsset = false;
  _.forEach(sources, function(source, index) {
    if(_.has(source, 'readOnlyMetadata') && _.get(source.readOnlyMetadata, 'status') === "Invalid"){
      inValidAsset = true;
    }
  });
  setHasInvalidAssets(inValidAsset);
}

useEffect(() => {
  checkForContentProtection();
  checkInvalidAssets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [sources])


const validateAssets = (jobType, sourceLocation, source, sourceList) => {
  const mapping = {
    "ASPERA": [{label: "Focus", value: "focusId"}, {label: "Asset/Inventory ID", value: "assetId"}],
    "J2K_MAM": [{label: "assetId", value: "xmnNo"}],
    "MORPH_ENCODING": [{label: "assetId", value: "assetId"}]
  };

  let clonedSources = [...sourceList];
  let sourceKey = _.get(source, "key");
  let validateField = _.get(sourceLocation, 'validateField');
  let validateFieldValue =  _.get(source, _.get(sourceLocation, 'validateField'));
  if (!validateFieldValue) {
    setSources(clonedSources);
    return;
  }
  let index = _.findIndex(clonedSources, (s) => _.get(s, 'key') === sourceKey);
  let isNoIDSource = (validateFieldValue && validateFieldValue === 'No ID Available');
  if (index === -1) {
    setSources(clonedSources);
    return;
  } else if (index > -1 && isNoIDSource) {
    delete source['readOnlyMetadata'];
    delete source['assetId'];
    clonedSources[index] = source;
    setSources(clonedSources);
    return;
  }
  let mappedValue = _.get(_.find(mapping[ _.get(sourceLocation, 'type')], (mapItem) =>
      _.get(mapItem, 'label') ===  (validateField === 'assetIdType' ? validateFieldValue : validateField)), 'value');
  let lastValidatedValue = _.get(source&&source.readOnlyMetadata, mappedValue);
  let currentValidatedValue = validateField === 'assetIdType' ? _.get(source, 'assetId') : validateFieldValue;
  let validatingSource = clonedSources[index];
  if (lastValidatedValue === currentValidatedValue){
    setSources(clonedSources);
    return;
  }
    
  validatingSource['readOnlyMetadata'] = {
    status: "Validating"
  };
  clonedSources[index] = validatingSource;
  setSources(clonedSources);
  
  

  let validationPayload = {jobType : jobType, idType: _.get(sourceLocation, 'type'), "assets" : [{[mappedValue] : currentValidatedValue}]};

  post({
    url: "assets",
    data: validationPayload
  })
  .then((response) => {
    delete source['readOnlyMetadata'];
    _.map(response, (metaData) => {
      if (index > -1){
        source['readOnlyMetadata'] = metaData;
      }
    })
  })
  .catch(() => {
    source['readOnlyMetadata'] = {
      status: "Invalid"
    };
  }).finally(()=>{
    let mapping = { ...validatedSources };
    mapping[sourceKey] = source;
    setValidatedSources(Object.assign({}, mapping));
  })
}

  const settingRequestor = () => {
    if(type === TYPES.REDO) {
      setRequestor(requestor);
    }else {
      setRequestor(UserUtils.transformUserInfo(userInfo))
    }
  }

  const requestDraft = () => {
    let isSubmitted = false;
    let status = Status.DRAFT;
    setSaved(true);
    let requestorForPayload = type === TYPES.REDO ? requestor : UserUtils.transformUserInfo(userInfo,true);
    settingRequestor();
    let requestPayload = getRequestPayload(isSubmitted, requestorForPayload);
    requestPayload["jobs"] = getJobsPayload(status, requestorForPayload);
    setJobs(requestPayload["jobs"]);
    setLoading(true);
      // It means it is the first POST call.
    if (_.isEmpty(requestId)) {
      post({
        url: config.apiUrls.requests + "create",
        data: requestPayload
      })
      .then((response) => {
        setCreatedDate(_.get(response, "createdDate", ""));
        setCreatedBy(_.get(response, "createdBy"));
        setRequestId(_.get(response, "requestId", null));
        setRecordId(_.get(response, "_id", null));
        const draftSocketMessage = SocketUtils.buildSocketMessage('NEW_REQUEST');
        _setMessageToSocket(draftSocketMessage);
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoading(false);
      })
    } else {
      put({
        url: config.apiUrls.requests + recordId,
        data: requestPayload
      })
      .then(() => {
      })
      .catch((error) => {
        console.log(error);        
      }).finally(()=>{
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      })
    }
  };

  const submitRequest = () => {
    let isSubmitted = true;
    let status = Status.SUBMITTED;

    let requestorForPayload = type === TYPES.REDO ? requestor : UserUtils.transformUserInfo(userInfo,true);
    let requestPayload = getRequestPayload(isSubmitted, requestorForPayload);

    requestPayload["jobs"] = getJobsPayload(status, requestorForPayload);
    requestPayload["user"] = UserUtils.transformUserInfo(userInfo, true);
    setLoading(true);
    put({
      url: config.apiUrls.requests + recordId,
      data: requestPayload
    })
    .then(() => {
      const submitJobSocketMessage = SocketUtils.buildSocketMessage('NEW_REQUEST');
      _setMessageToSocket(submitJobSocketMessage)
      history.push("/");
    })
    .catch((error) => {
      console.log(error)
    }).finally(()=>{
      setLoading(false);
    })
    .finally(() => {
      setLoading(false);
    })
  };

  const _setLoadId = (id) => {
    setLoadId(id);
  };

  const updateJob = () => {
    let dest = {};
    dest["deliveryOptions"] = deliveries;
    dest["sourceToDeliveryMapping"] = deliveriesToSourceMapping;
    dest["jobKeysToFilename"] = jobKeysToFilename;
    let jobModel = JobUtils.updateJob(
      requestId,
      jobId,
      requestName,
      dueDate,
      dueTime,
      JobUtils.extractJobTypeProperty(jobType, "jobType"),
      JobUtils.extractSourceLocationProperty(sourceLocation, "type"),
      notes,
      contacts,
      status,
      sources,
      _.nth(processes, 0),
      dest,
      auditTrail,
      requestor,
      assignedTo,
      createdDate,
      null,
      jobRecordId,
      needsSourceOverride,
      createdBy,
      qcRequired,
      sourceLocation,
      jobType,
      contentProtection,
      stitchAssetName,
      isStitch,
      stitchedKeys,
      faspexAccountMapping
    );
    jobModel["requests"] = [getRequestPayload(true, requestor)];
    jobModel["requestSummary"] = requestSummary;
    setLoading(true);
    let req = Object.assign({}, JSON.parse(JSON.stringify(jobModel)));
    req["user"] = UserUtils.transformUserInfo(userInfo, true);
    put({
      url: config.apiUrls.jobs + jobRecordId,
      data: req,
    })
    .then((response) => {
      let id = _.get(response, "_id");
      const updateJobSocketMessage = SocketUtils.buildSocketMessage('UPDATE_JOB');
      _setMessageToSocket(updateJobSocketMessage);
      history.push(_.isNil(id) ? "/" : "/summary?id=" + id);
    })
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      setLoading(false);
    })
  };
  const _setLoading = (isLoading: boolean) => {
    setLoading(isLoading);
  };  

  const loadParameters = () => {
    let queryString = window.location.search;
    let urlParams = new URLSearchParams(queryString);
    let _requestId = urlParams.get("requestId");
    let _jobId = urlParams.get("jobId");
    let _type = urlParams.get("type");

    _setType(_.isEmpty(_type) ? initialState.type : _type);
    if (!_.isEmpty(_requestId)) {
      _setDisplay(false);
      _setLoadId({ requestId: _requestId });
    }
    if (!_.isEmpty(_jobId)) {
      _setDisplay(false);
      _setLoadId({ jobId: _jobId });
    }
    if (_.isEmpty(_requestId) && _.isEmpty(_jobId)) {
      resetContext();
    }
    return () => {
      _setLoading(true);
    };
  }

  const resetContext = () => {
    setHasInvalidAssets(initialState.hasInvalidAssets);
    setNeedContentProtection(initialState.needContentProtection);
    setLoadId(initialState.loadId);
    setRecordId(initialState.recordId);
    setJobRecordId(initialState.jobRecordId);
    setRequestId(initialState.requestId);
    setJobId(initialState.jobId);
    setRequestName(initialState.requestName);
    setDueDate(getMinDate());
    setDueTime(initialState.dueTime);
    setJobType(initialState.jobType);
    setSourceLocation(initialState.sourceLocation);
    setContacts(initialState.contacts);
    setNotes(initialState.notes);
    setStatus(initialState.status);
    setRequestor(initialState.requestor);
    setAssignedTo(initialState.assignedTo);
    setCreatedDate(initialState.createdDate);
    setSources(initialState.sources);
    setProcesses(initialState.processes);
    setDeliveries(initialState.deliveries);
    setDestination(initialState.destination);
    setDeliveriesToSourceMapping(initialState.deliveriesToSourceMapping);
    setJobKeysToFilename(initialState.jobKeysToFilename);
    setJobs(initialState.jobs);
    setAuditTrail(initialState.auditTrail);
    setSubmittedDate(initialState.submittedDate);
    setUnMappedSources(initialState.unMappedSources);
    setNeedsSourceOverride(initialState.needsSourceOverride);
    setSaved(initialState.draftSaved);
    setRequestSummary(initialState.requestSummary);
    setCreatedBy(initialState.createdBy);
    setItemList(initialState.itemList);
    setQcRequired(initialState.qcRequired);
    setShowSourceForm(initialState.showSourceForm);
    setSelectedSource(initialState.selectedSource);
    setShowProcessForm(initialState.showProcessForm);
    setSelectedProcess(initialState.selectedProcess);
    setShowDeliveryForm(initialState.showDeliveryForm);
    setSelectedDelivery(initialState.selectedDelivery);
    setIsStitch(initialState.isStitch);
    setStitchAssetName(initialState.stitchAssetName);
    setStitchedKeys(initialState.stitchedKeys);
    setStitchAssetNameError(initialState.stitchAssetNameError);
    setStitchingError(initialState.stitchingError);
    setValidatedSources(initialState.validatedSources);
    setEcoProcessError(initialState.ecoProcessError);
    setEventDeliveryFlag(initialState.eventDeliveryFlag);    
    setImportError(initialState.importError);
    setDownloading(initialState.downloading);
    setEnableSnackbar(initialState.enableSnackbar);
    setSnackbarMessage(initialState.snackbarMessage);
  }

  const trimObjectValues = (obj) => {
    Object.keys(obj).forEach(k => obj[k] = typeof obj[k] === "string" ? obj[k].trim() : obj[k])
  }

  return (
    <RequestContext.Provider
      value={{
        needContentProtection,
        hasInvalidAssets,
        loading,
        _setLoading,
        loadId,
        _setLoadId,
        jobId,
        _setJobId,
        requestId,
        _setRequestId,
        requestName,
        _setRequestName,
        dueDate,
        _setDueDate,
        dueTime,
        _setDueTime,
        jobType,
        _setJobType,
        sourceLocation,
        _setSourceLocation,
        contacts,
        _setContacts,
        notes,
        _setNotes,
        status,
        _setStatus,
        deliveriesToSourceMapping,
        _setDeliveriesToSourceMapping,
        jobKeysToFilename,
        _setJobKeysToFilename,
        requestor,
        assignedTo: {},
        createdDate,
        sources,
        processes,
        deliveries,
        destination,
        itemList,
        getItemList,
        jobs,
        auditTrail,
        addNewSource,
        addNewProcess,
        addNewProcesses,
        addNewDelivery,
        addNewDeliveries,
        updateSource,
        updateProcess,
        updateDelivery,
        addNewSources,
        requestDraft,
        submitRequest,
        onLoadRequest,
        updateJob,
        deleteSource,
        deleteDelivery,
        isStitch,
        setIsStitch,
        stitchAssetName,
        setStitchAssetName:_setStitchAssetName,
        stitchedKeys,
        setStitchedKeys,
        stitchAssetNameError,
        setStitchAssetNameError,
        stitchingError,
        setStitchingError,
        deleteProcess,
        unMappedSources,
        _setUnMappedSources,
        needsSourceOverride,
        showRequestForm,
        _setShowRequestForm,
        draftSaved,
        submittedDate,
        requestSummary,
        display,
        _setDisplay,
        createdBy,
        qcRequired,
        contentProtection,
        _setContentProtection,
        type,
        _setType,
        _setShowSourceForm,
        showSourceForm,
        _setSelectedSource,
        selectedSource,
        showProcessForm,
        _setShowProcessForm,
        _setSelectedProcess,
        selectedProcess,
        _setShowDeliveryForm,
        showDeliveryForm,
        _setSelectedDelivery,
        selectedDelivery,
        ecoProcessError,
        _setEcoProcessError,
        loadParameters,
        unmappedDeliveryKeys,
        _setUnmappedDeliveryKeys,
        premiereDateConfirmed,
        setPremiereDateConfirmed,
        dcpDateConfirmed,
        setDcpDateConfirmed,
        eventDeliveryFlag,
        setEventDeliveryFlag,
        importError,
        setImportError,
        downloading,
        snackbarMessage,
        enableSnackbar,
        _setEnableSnackbar,
        getTemplate,
        deliveryMetadataKey,
        _setDeliveryMetadataKey
      }}>
      {_.includes([UserRoles.CR_API, UserRoles.ADMINWITHREADACCESS, UserRoles.MEDIAREQUEST, UserRoles.LEGACY_GMSC], _.toLower(userRole)) ? <ErrorRedirect error={{
        errorCode: "access_denied",
        type: "restricted",
        message: "User is not authorized to access this page.",
        redirect: _.toLower(userRole) === UserRoles.CR_API ? true : false
      }}
      /> : children}
      <LoadingSpinner show={loading || !display || isUserLoading} />
    </RequestContext.Provider>
  );
};
